import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Paper, Typography, TextField, Button, Grid, Alert,
  CircularProgress, Autocomplete, Divider, Card, CardContent, Stack,
  ToggleButton, ToggleButtonGroup, FormControlLabel, Switch,
  Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem, Checkbox, ListItemText,
} from '@mui/material';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import PrintIcon from '@mui/icons-material/Print';
import SearchIcon from '@mui/icons-material/Search';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import GradeIcon from '@mui/icons-material/Grade';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import BarcodeCameraPanel from '../../components/BarcodeCameraPanel';
import ScanExamineConfirmDialog from '../../components/ScanExamineConfirmDialog';
import ScanGradeDialog from '../../components/ScanGradeDialog';
import AdminPageHeader from '../../next/AdminPageHeader.jsx';

const TRACTATE_NAMES = [
  'ברכות','שבת','עירובין','פסחים','יומא','סוכה','ביצה','ראש השנה','תענית',
  'מגילה','מועד קטן','חגיגה','יבמות','כתובות','נדרים','נזיר','סוטה','גיטין',
  'קידושין','בבא קמא','בבא מציעא','בבא בתרא','סנהדרין','מכות','שבועות',
  'עבודה זרה','הוריות','זבחים','מנחות','חולין','בכורות','ערכין','תמורה',
  'כריתות','מעילה','תמיד','נידה',
];

const AUTO_EXAMINE_KEY = 'labels.scan.autoExamine';
const AUTO_REGISTER_KEY = 'labels.scan.autoRegister';
const IS_OPEN_GEMARA = process.env.REACT_APP_EXAM_MODE === 'open-gemara';

async function downloadBlob(path, body, fallbackName, mimeType) {
  const { data } = await api.post(path, body || {}, {
    responseType: 'blob',
  });
  const blob = new Blob([data], { type: mimeType });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fallbackName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

async function downloadPdf(path, body, fallbackName) {
  await downloadBlob(path, body, fallbackName, 'application/pdf');
}

async function downloadZip(path, body, fallbackName) {
  await downloadBlob(path, body, fallbackName, 'application/zip');
}

async function parseExportError(err, fallback) {
  let message = fallback;
  const data = err.response?.data;
  if (data instanceof Blob) {
    try {
      const text = await data.text();
      const parsed = JSON.parse(text);
      message = parsed.error || message;
    } catch { /* keep default */ }
  } else if (data?.error) {
    message = data.error;
  }
  return message;
}

function defaultExportPeriodIds(periods) {
  const sorted = [...periods].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const active = sorted.find((p) => p.isActive) || sorted[sorted.length - 1] || null;
  if (!active) return [];
  const prev = sorted.filter((p) => (p.order ?? 0) < (active.order ?? 0)).at(-1);
  return [prev, active].filter(Boolean).map((p) => String(p._id));
}

/** ממיר תוצאת lookup לרשימת מועמדים אחידה לסימון/ציון */
function buildCandidates(lookup) {
  if (!lookup) return [];
  if (lookup.kind === 'exam') {
    if (!lookup.registrationId) return [];
    return [{
      registrationId: lookup.registrationId,
      exam: lookup.exam || null,
      periodId: lookup.period?._id || lookup.registration?.periodId || lookup.exam?.periodId || null,
      period: lookup.period || null,
      examined: !!lookup.examined,
      gemaraSelection: lookup.gemaraSelection || lookup.registration?.gemaraSelection || [],
    }];
  }
  return (lookup.registrations || [])
    .filter((r) => r?._id)
    .map((r) => ({
      registrationId: r._id,
      exam: r.exam || null,
      periodId: r.periodId || r.exam?.periodId || null,
      period: r.period || null,
      examined: !!r.examined,
      gemaraSelection: r.gemaraSelection || [],
    }));
}

function isFullyExamined(candidate) {
  if (!candidate) return false;
  const gemara = candidate.gemaraSelection || [];
  if (gemara.length > 0) return gemara.every((s) => s.examined);
  return !!candidate.examined;
}

const LabelsPage = () => {
  const { hasPermission } = useAuth();
  const canExamine = hasPermission('examRegistration', 'create');
  const canGrade = hasPermission('grades', 'create');

  const [scanMode, setScanMode] = useState('lookup');
  const [autoExamine, setAutoExamine] = useState(() => {
    try {
      return localStorage.getItem(AUTO_EXAMINE_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [autoRegister, setAutoRegister] = useState(() => {
    try {
      return !IS_OPEN_GEMARA && localStorage.getItem(AUTO_REGISTER_KEY) === '1';
    } catch {
      return false;
    }
  });

  const [barcodeInput, setBarcodeInput] = useState('');
  const [lookupResult, setLookupResult] = useState(null);
  const [lookupError, setLookupError] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);
  const [actionSuccess, setActionSuccess] = useState('');

  const [cameraActive, setCameraActive] = useState(false);
  const [examineDialogOpen, setExamineDialogOpen] = useState(false);
  const [examineCandidates, setExamineCandidates] = useState([]);
  const [examineMember, setExamineMember] = useState(null);
  const [examineSaving, setExamineSaving] = useState(false);

  const [gradeDialogOpen, setGradeDialogOpen] = useState(false);
  const [gradeCandidates, setGradeCandidates] = useState([]);
  const [gradeMember, setGradeMember] = useState(null);

  const [tractateFilter, setTractateFilter] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [printLoading, setPrintLoading] = useState(false);
  const [printError, setPrintError] = useState('');

  const [exportKolelLoading, setExportKolelLoading] = useState(false);
  const [exportExamLoading, setExportExamLoading] = useState(false);
  const [exportLocationLoading, setExportLocationLoading] = useState(false);
  const [exportLocationKolelLoading, setExportLocationKolelLoading] = useState(false);
  const [exportError, setExportError] = useState('');
  const [barcodeSvg, setBarcodeSvg] = useState('');
  const [periods, setPeriods] = useState([]);
  const [examExportDialogOpen, setExamExportDialogOpen] = useState(false);
  const [exportPeriodIds, setExportPeriodIds] = useState([]);
  const [stickerLayout, setStickerLayout] = useState(24);

  const barcodeRef = useRef(null);

  const availableModes = useMemo(() => {
    const modes = ['lookup'];
    if (canExamine) modes.push('examine');
    if (canGrade) modes.push('grade');
    return modes;
  }, [canExamine, canGrade]);

  useEffect(() => {
    if (!availableModes.includes(scanMode)) {
      setScanMode('lookup');
    }
  }, [availableModes, scanMode]);

  useEffect(() => {
    try {
      localStorage.setItem(AUTO_EXAMINE_KEY, autoExamine ? '1' : '0');
    } catch { /* ignore */ }
  }, [autoExamine]);

  useEffect(() => {
    if (IS_OPEN_GEMARA) return;
    try {
      localStorage.setItem(AUTO_REGISTER_KEY, autoRegister ? '1' : '0');
    } catch { /* ignore */ }
  }, [autoRegister]);

  useEffect(() => {
    let cancelled = false;
    api.get('/exam-periods').then(({ data }) => {
      if (cancelled) return;
      const list = Array.isArray(data?.data) ? data.data : (Array.isArray(data) ? data : []);
      setPeriods(list);
    }).catch(() => {
      if (!cancelled) setPeriods([]);
    });
    return () => { cancelled = true; };
  }, []);

  const focusBarcode = useCallback(() => {
    setTimeout(() => barcodeRef.current?.focus?.(), 50);
  }, []);

  const resetAfterAction = useCallback((message) => {
    setBarcodeInput('');
    setLookupResult(null);
    setLookupError('');
    setActionSuccess(message || '');
    focusBarcode();
  }, [focusBarcode]);

  useEffect(() => {
    let cancelled = false;
    const code = lookupResult?.barcode;
    if (!code) {
      setBarcodeSvg('');
      return undefined;
    }
    (async () => {
      try {
        const { data } = await api.get(`/labels/barcode/${encodeURIComponent(code)}`, {
          responseType: 'text',
          headers: { Accept: 'image/svg+xml' },
        });
        if (!cancelled) setBarcodeSvg(data);
      } catch {
        if (!cancelled) setBarcodeSvg('');
      }
    })();
    return () => { cancelled = true; };
  }, [lookupResult?.barcode]);

  const markExamined = useCallback(async ({
    registrationId,
    examined = true,
    gemaraExamined,
    gemaraSelection,
  }) => {
    let body;
    if (gemaraSelection) {
      body = { gemaraSelection };
    } else if (gemaraExamined) {
      body = { gemaraExamined };
    } else {
      body = { examined: !!examined };
    }
    await api.put(`/registrations/${registrationId}`, body);
  }, []);

  const openExamineDialog = useCallback((member, candidates) => {
    setExamineMember(member);
    setExamineCandidates(candidates);
    setExamineDialogOpen(true);
  }, []);

  const openGradeDialog = useCallback((member, candidates) => {
    setGradeMember(member);
    setGradeCandidates(candidates);
    setGradeDialogOpen(true);
  }, []);

  const handleExamineFromLookup = useCallback(async (data) => {
    const candidates = buildCandidates(data);
    if (!candidates.length) {
      setLookupError('לא נמצאה הרשמה לסימון כנבחן');
      setLookupResult(data);
      return;
    }

    const needsChoice = candidates.length > 1
      || candidates.some((c) => (c.gemaraSelection || []).length > 1);

    if (!autoExamine || needsChoice) {
      openExamineDialog(data.member, candidates);
      setLookupResult(data);
      return;
    }

    const candidate = candidates[0];
    if (isFullyExamined(candidate)) {
      resetAfterAction(
        `${data.member.firstName || ''} ${data.member.lastName || ''}`.trim()
          + ' כבר מסומן כנבחן'
      );
      return;
    }

    try {
      setLookupLoading(true);
      const gemara = candidate.gemaraSelection || [];
      if (gemara.length > 0) {
        await markExamined({
          registrationId: candidate.registrationId,
          gemaraExamined: gemara.map((sel) => ({ tractate: sel.tractate, examined: true })),
        });
      } else {
        await markExamined({ registrationId: candidate.registrationId, examined: true });
      }
      const name = `${data.member.firstName || ''} ${data.member.lastName || ''}`.trim();
      resetAfterAction(`${name || 'החבר'} סומן כנבחן`);
    } catch (err) {
      setLookupError(err.response?.data?.error || 'שגיאה בסימון נבחן');
      setLookupResult(data);
    } finally {
      setLookupLoading(false);
    }
  }, [autoExamine, markExamined, openExamineDialog, resetAfterAction]);

  const handleGradeFromLookup = useCallback((data) => {
    const candidates = buildCandidates(data);
    if (!candidates.length) {
      setLookupError('לא נמצאה הרשמה להזנת ציון');
      setLookupResult(data);
      return;
    }
    setLookupResult(data);
    openGradeDialog(data.member, candidates);
  }, [openGradeDialog]);

  const processLookupResult = useCallback(async (data, mode) => {
    setActionSuccess('');
    if (mode === 'examine') {
      await handleExamineFromLookup(data);
      return;
    }
    if (mode === 'grade') {
      handleGradeFromLookup(data);
      return;
    }
    setLookupResult(data);
  }, [handleExamineFromLookup, handleGradeFromLookup]);

  const handleScanExamineRegister = useCallback(async (code) => {
    try {
      setLookupLoading(true);
      const { data } = await api.post('/labels/scan-examine', {
        code,
        registerIfMissing: true,
      });
      const name = `${data.member?.firstName || ''} ${data.member?.lastName || ''}`.trim();
      const who = name || 'החבר';
      let message;
      if (data.created || data.reactivated) {
        message = `${who} נרשם וסומן כנבחן`;
      } else if (data.alreadyExamined) {
        message = `${who} כבר מסומן כנבחן`;
      } else {
        message = `${who} סומן כנבחן`;
      }
      resetAfterAction(message);
    } catch (err) {
      setLookupError(err.response?.data?.error || 'שגיאה בסימון נבחן');
      setLookupResult(null);
    } finally {
      setLookupLoading(false);
      focusBarcode();
    }
  }, [resetAfterAction, focusBarcode]);

  const runBarcodeAction = useCallback(async (rawCode, modeOverride) => {
    const code = String(rawCode || '').trim();
    if (!code) return;
    const mode = modeOverride || scanMode;

    setLookupError('');
    setLookupResult(null);
    setActionSuccess('');

    if (mode === 'examine' && autoRegister && !IS_OPEN_GEMARA) {
      await handleScanExamineRegister(code);
      return;
    }

    setLookupLoading(true);
    try {
      const { data } = await api.post('/labels/lookup-barcode', { code });
      await processLookupResult(data, mode);
    } catch (err) {
      setLookupError(err.response?.data?.error || 'שגיאה בחיפוש');
    } finally {
      setLookupLoading(false);
      if (mode === 'lookup') focusBarcode();
    }
  }, [scanMode, autoRegister, processLookupResult, focusBarcode, handleScanExamineRegister]);

  const handleBarcodeLookup = () => runBarcodeAction(barcodeInput);

  const handleBarcodeKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleBarcodeLookup();
    }
  };

  const handleCameraScan = useCallback((code) => {
    setBarcodeInput(code);
    runBarcodeAction(code);
  }, [runBarcodeAction]);

  const cameraPaused = examineDialogOpen || gradeDialogOpen || lookupLoading;

  const handleExamineConfirm = async ({ registrationId, examined, gemaraExamined, gemaraSelection }) => {
    setExamineSaving(true);
    setLookupError('');
    try {
      await markExamined({ registrationId, examined, gemaraExamined, gemaraSelection });
      setExamineDialogOpen(false);
      const name = `${examineMember?.firstName || ''} ${examineMember?.lastName || ''}`.trim();
      resetAfterAction(`${name || 'החבר'} סומן כנבחן`);
    } catch (err) {
      setLookupError(err.response?.data?.error || 'שגיאה בסימון נבחן');
    } finally {
      setExamineSaving(false);
    }
  };

  const handleGradeSaved = () => {
    const name = `${gradeMember?.firstName || ''} ${gradeMember?.lastName || ''}`.trim();
    resetAfterAction(`הציון ל${name || 'חבר'} נשמר`);
  };

  const handlePrintLabels = async () => {
    setPrintLoading(true);
    setPrintError('');
    try {
      const body = { layout: stickerLayout };
      if (tractateFilter) body.tractate = tractateFilter;
      if (cityFilter) body.city = cityFilter;

      const { data } = await api.post('/labels/print', body, {
        responseType: 'text',
        headers: { Accept: 'text/html' },
      });

      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(data);
        printWindow.document.close();
        printWindow.onload = () => printWindow.print();
      }
    } catch (err) {
      setPrintError(err.response?.data?.error || 'שגיאה ביצירת מדבקות');
    } finally {
      setPrintLoading(false);
    }
  };

  const handleExportByKolel = async () => {
    setExportKolelLoading(true);
    setExportError('');
    try {
      await downloadPdf('/labels/export-by-kolel', { layout: stickerLayout }, 'ברקודים-לפי-כולל.pdf');
    } catch (err) {
      setExportError(await parseExportError(err, 'שגיאה בייצוא לפי כוללים'));
    } finally {
      setExportKolelLoading(false);
    }
  };

  const openExamExportDialog = () => {
    setExportError('');
    setExportPeriodIds(defaultExportPeriodIds(periods));
    setExamExportDialogOpen(true);
  };

  const handleExportByExam = async () => {
    if (exportPeriodIds.length === 0) {
      setExportError('יש לבחור לפחות מועד אחד');
      return;
    }
    setExportExamLoading(true);
    setExportError('');
    try {
      await downloadZip('/labels/export-by-exam', { periodIds: exportPeriodIds, layout: stickerLayout }, 'ברקודים-לפי-מבחן.zip');
      setExamExportDialogOpen(false);
    } catch (err) {
      setExportError(await parseExportError(err, 'שגיאה בייצוא לפי מבחנים'));
    } finally {
      setExportExamLoading(false);
    }
  };

  const handleExportByLocation = async () => {
    setExportLocationLoading(true);
    setExportError('');
    try {
      await downloadZip('/labels/export-by-location', { layout: stickerLayout }, 'ברקודים-לפי-מוקד.zip');
    } catch (err) {
      setExportError(await parseExportError(err, 'שגיאה בייצוא לפי מוקדי בחינה'));
    } finally {
      setExportLocationLoading(false);
    }
  };

  const handleExportByLocationKolel = async () => {
    setExportLocationKolelLoading(true);
    setExportError('');
    try {
      await downloadZip('/labels/export-by-location-kolel', { layout: stickerLayout }, 'ברקודים-לפי-מוקד-וכולל.zip');
    } catch (err) {
      setExportError(await parseExportError(err, 'שגיאה בייצוא לפי מוקד וקוד כולל'));
    } finally {
      setExportLocationKolelLoading(false);
    }
  };

  const actionButtonLabel = scanMode === 'examine'
    ? 'סמן'
    : scanMode === 'grade'
      ? 'הזן ציון'
      : 'חפש';

  const actionButtonIcon = lookupLoading
    ? <CircularProgress size={16} />
    : scanMode === 'examine'
      ? <HowToRegIcon />
      : scanMode === 'grade'
        ? <GradeIcon />
        : <SearchIcon />;

  return (
    <Box>
      <AdminPageHeader
        title="מדבקות וברקודים"
        sub="סריקת ברקוד מבחן (קודאישי-קודמבחן), סימון נבחן, הזנת ציון, ייצוא PDF והדפסת מדבקות"
        legacy={(
          <>
            <Typography variant="h5" fontWeight={700} gutterBottom>
              מדבקות וברקודים
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              סריקת ברקוד מבחן (קודאישי-קודמבחן), סימון נבחן, הזנת ציון, ייצוא PDF והדפסת מדבקות
            </Typography>
          </>
        )}
      />

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              <QrCodeScannerIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
              סריקת ברקוד
            </Typography>

            {availableModes.length > 1 && (
              <ToggleButtonGroup
                exclusive
                fullWidth
                size="small"
                value={scanMode}
                onChange={(_e, val) => {
                  if (val) {
                    setScanMode(val);
                    setLookupError('');
                    setActionSuccess('');
                    focusBarcode();
                  }
                }}
                sx={{ mb: 2 }}
              >
                <ToggleButton value="lookup">חיפוש</ToggleButton>
                {canExamine && <ToggleButton value="examine">סמן נבחן</ToggleButton>}
                {canGrade && <ToggleButton value="grade">הזנת ציון</ToggleButton>}
              </ToggleButtonGroup>
            )}

            {scanMode === 'examine' && (
              <Stack spacing={0.5} sx={{ mb: 1 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={autoExamine}
                      onChange={(e) => setAutoExamine(e.target.checked)}
                      size="small"
                      disabled={autoRegister && !IS_OPEN_GEMARA}
                    />
                  }
                  label="סימון אוטומטי (בלי אישור)"
                  sx={{ display: 'flex' }}
                />
                {!IS_OPEN_GEMARA && (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={autoRegister}
                        onChange={(e) => setAutoRegister(e.target.checked)}
                        size="small"
                      />
                    }
                    label="רישום אוטומטי אם חסר"
                    sx={{ display: 'flex' }}
                  />
                )}
              </Stack>
            )}

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              פורמט: קוד אישי-קוד מבחן (למשל 1234-56). ניתן גם לקוד אישי בלבד.
              סורק חומרה, הקלדה או מצלמה.
            </Typography>

            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
              <TextField
                inputRef={barcodeRef}
                fullWidth
                size="small"
                label="ברקוד / קוד אישי"
                value={barcodeInput}
                onChange={(e) => setBarcodeInput(e.target.value)}
                onKeyDown={handleBarcodeKeyDown}
                placeholder="1234-56"
                autoFocus
                sx={{ flex: '1 1 160px' }}
              />
              <Button
                variant="contained"
                onClick={handleBarcodeLookup}
                disabled={lookupLoading}
                startIcon={actionButtonIcon}
              >
                {actionButtonLabel}
              </Button>
              <Button
                variant={cameraActive ? 'contained' : 'outlined'}
                color={cameraActive ? 'secondary' : 'primary'}
                onClick={() => setCameraActive((v) => !v)}
                startIcon={<CameraAltIcon />}
              >
                {cameraActive ? 'עצור מצלמה' : 'מצלמה'}
              </Button>
            </Box>

            {lookupError && <Alert severity="error" sx={{ mb: 2 }}>{lookupError}</Alert>}
            {actionSuccess && <Alert severity="success" sx={{ mb: 2 }}>{actionSuccess}</Alert>}

            <BarcodeCameraPanel
              active={cameraActive}
              paused={cameraPaused}
              onScan={handleCameraScan}
              onStop={() => setCameraActive(false)}
            />

            {lookupResult && scanMode === 'lookup' && (
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={700}>
                    {lookupResult.member.firstName} {lookupResult.member.lastName}
                  </Typography>
                  {lookupResult.member.fatherName && (
                    <Typography variant="body2" color="text.secondary">
                      בן {lookupResult.member.fatherName}
                    </Typography>
                  )}
                  <Typography variant="body2" color="text.secondary">
                    קוד אישי: {lookupResult.member.personalCode}
                  </Typography>
                  {(lookupResult.member.kolelRef?.name || lookupResult.member.kolel) && (
                    <Typography variant="body2" color="text.secondary">
                      כולל: {lookupResult.member.kolelRef?.name || lookupResult.member.kolel}
                    </Typography>
                  )}
                  {lookupResult.member.phone && (
                    <Typography variant="body2" color="text.secondary">
                      טלפון: {lookupResult.member.phone}
                    </Typography>
                  )}
                  {lookupResult.member.city && (
                    <Typography variant="body2" color="text.secondary">
                      עיר: {lookupResult.member.city}
                    </Typography>
                  )}

                  {lookupResult.kind === 'exam' && lookupResult.exam && (
                    <>
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="subtitle2" fontWeight={700}>
                        מבחן {lookupResult.exam.code} — {lookupResult.exam.name}
                      </Typography>
                      {lookupResult.exam.material && (
                        <Typography variant="body2" color="text.secondary">
                          {lookupResult.exam.material}
                        </Typography>
                      )}
                      {lookupResult.period?.name && (
                        <Typography variant="body2" color="text.secondary">
                          זמן: {lookupResult.period.name}
                        </Typography>
                      )}
                      {lookupResult.registrationId && (
                        <Typography variant="body2" color="text.secondary">
                          נבחן: {lookupResult.examined
                            || (lookupResult.gemaraSelection || []).some((s) => s.examined)
                            ? 'כן'
                            : 'לא'}
                        </Typography>
                      )}
                    </>
                  )}

                  {lookupResult.kind === 'personal' && lookupResult.registrations?.length > 0 && (
                    <>
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="subtitle2" fontWeight={600}>
                        הרשמות פעילות ({lookupResult.registrations.length})
                      </Typography>
                      {lookupResult.registrations.map((reg) => (
                        <Typography key={reg._id} variant="body2" color="text.secondary">
                          {reg.exam
                            ? `${reg.exam.code != null ? `[${reg.exam.code}] ` : ''}${reg.exam.name}${reg.exam.material ? ` — ${reg.exam.material}` : ''}`
                            : (reg.gemaraSelection?.map((sel) =>
                              `${sel.tractate} ${sel.ranges?.map((r) => `${r.from}-${r.to}`).join(', ')}`
                            ).join(' | ') || 'ללא פרטים')}
                          {reg.examined || (reg.gemaraSelection || []).some((s) => s.examined)
                            ? ' — נבחן'
                            : ''}
                        </Typography>
                      ))}
                    </>
                  )}

                  {lookupResult.barcode && (
                    <>
                      <Divider sx={{ my: 1 }} />
                      <Box sx={{ textAlign: 'center', mt: 1 }}>
                        <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                          {lookupResult.barcode}
                        </Typography>
                        {barcodeSvg ? (
                          <Box
                            sx={{ display: 'inline-block', maxWidth: '100%', '& svg': { maxWidth: '100%', height: 50 } }}
                            dangerouslySetInnerHTML={{ __html: barcodeSvg }}
                          />
                        ) : null}
                      </Box>
                    </>
                  )}
                </CardContent>
              </Card>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Stack spacing={3}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                פורמט מדבקות לדף
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                חל על הדפסה ועל כל ייצואי ה-PDF (TANEX 70מ״מ)
              </Typography>
              <ToggleButtonGroup
                exclusive
                fullWidth
                size="small"
                value={stickerLayout}
                onChange={(_e, value) => { if (value != null) setStickerLayout(value); }}
                aria-label="פורמט מדבקות לדף"
              >
                <ToggleButton value={21}>21 מדבקות</ToggleButton>
                <ToggleButton value={24}>24 מדבקות</ToggleButton>
                <ToggleButton value={33}>33 מדבקות</ToggleButton>
              </ToggleButtonGroup>
            </Paper>

            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                <PictureAsPdfIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                ייצוא ברקודים ל-PDF
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                כוללים: זמן פעיל + שעבר. מבחנים/מוקדים/מוקד+כולל: קובץ נפרד לכל עיר; בתוך מוקד+כולל דף לכל קוד כולל.
              </Typography>
              <Stack spacing={1.5}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleExportByKolel}
                  disabled={exportKolelLoading}
                  startIcon={exportKolelLoading ? <CircularProgress size={16} /> : <PictureAsPdfIcon />}
                >
                  ייצוא לפי כוללים (זמן פעיל + שעבר)
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={openExamExportDialog}
                  disabled={exportExamLoading}
                  startIcon={exportExamLoading ? <CircularProgress size={16} /> : <PictureAsPdfIcon />}
                >
                  ייצוא לפי קודי מבחן (בחירת מועדים)
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={handleExportByLocation}
                  disabled={exportLocationLoading}
                  startIcon={exportLocationLoading ? <CircularProgress size={16} /> : <PictureAsPdfIcon />}
                >
                  ייצוא לפי מוקדי בחינה (קובץ לכל עיר)
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={handleExportByLocationKolel}
                  disabled={exportLocationKolelLoading}
                  startIcon={exportLocationKolelLoading ? <CircularProgress size={16} /> : <PictureAsPdfIcon />}
                >
                  ייצוא לפי מוקדים + קוד כולל (קובץ לכל עיר)
                </Button>
              </Stack>
              {exportError && <Alert severity="error" sx={{ mt: 2 }}>{exportError}</Alert>}
            </Paper>

            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                <PrintIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                הדפסת מדבקות
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                סינון חברים והדפסת מדבקות עם קוד אישי וברקוד
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Autocomplete
                    options={TRACTATE_NAMES}
                    value={tractateFilter || null}
                    onChange={(_e, val) => setTractateFilter(val || '')}
                    renderInput={(params) => (
                      <TextField {...params} label="סינון לפי מסכת" size="small" />
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="סינון לפי עיר"
                    value={cityFilter}
                    onChange={(e) => setCityFilter(e.target.value)}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={handlePrintLabels}
                    disabled={printLoading}
                    startIcon={printLoading ? <CircularProgress size={16} /> : <PrintIcon />}
                  >
                    הדפס מדבקות
                  </Button>
                </Grid>
              </Grid>

              {printError && <Alert severity="error" sx={{ mt: 2 }}>{printError}</Alert>}
            </Paper>
          </Stack>
        </Grid>
      </Grid>

      <ScanExamineConfirmDialog
        open={examineDialogOpen}
        onClose={() => setExamineDialogOpen(false)}
        member={examineMember}
        candidates={examineCandidates}
        onConfirm={handleExamineConfirm}
        saving={examineSaving}
      />

      <ScanGradeDialog
        open={gradeDialogOpen}
        onClose={() => {
          setGradeDialogOpen(false);
          focusBarcode();
        }}
        member={gradeMember}
        candidates={gradeCandidates}
        onSaved={handleGradeSaved}
      />

      <Dialog
        open={examExportDialogOpen}
        onClose={() => !exportExamLoading && setExamExportDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>ייצוא ברקודים לפי קוד מבחן</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              בחרו מועדים להנפקה. כל מועד וקוד מבחן יופיעו בדף נפרד, וקובץ נפרד לכל עיר.
            </Typography>
            <FormControl fullWidth size="small">
              <InputLabel id="labels-export-periods-label">מועדים</InputLabel>
              <Select
                labelId="labels-export-periods-label"
                label="מועדים"
                multiple
                value={exportPeriodIds}
                onChange={(e) => setExportPeriodIds(
                  typeof e.target.value === 'string'
                    ? e.target.value.split(',')
                    : e.target.value
                )}
                disabled={exportExamLoading}
                renderValue={(selected) => {
                  const labels = selected.map((id) => {
                    const p = periods.find((x) => String(x._id) === String(id));
                    return p?.name || id;
                  });
                  return labels.join(', ');
                }}
              >
                {periods.map((p) => (
                  <MenuItem key={p._id} value={String(p._id)}>
                    <Checkbox checked={exportPeriodIds.includes(String(p._id))} size="small" />
                    <ListItemText primary={`${p.name}${p.isActive ? ' (פעיל)' : ''}`} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                onClick={() => setExportPeriodIds(periods.map((p) => String(p._id)))}
                disabled={exportExamLoading}
              >
                הכל
              </Button>
              <Button
                size="small"
                onClick={() => setExportPeriodIds(defaultExportPeriodIds(periods))}
                disabled={exportExamLoading}
              >
                פעיל + שעבר
              </Button>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExamExportDialogOpen(false)} disabled={exportExamLoading}>
            ביטול
          </Button>
          <Button
            variant="contained"
            onClick={handleExportByExam}
            disabled={exportExamLoading || exportPeriodIds.length === 0}
            startIcon={exportExamLoading ? <CircularProgress size={16} /> : <PictureAsPdfIcon />}
          >
            הורד ZIP
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LabelsPage;
